import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
/**
 * phase-a5-assistant-admin-audit-retention — admin Assistant Activity view.
 *
 * Every assistant Q&A turn with user, scope, model, tokens, and timestamp —
 * searchable/filterable, tenant-scoped server-side, gated on AuditLog:read
 * (the same read gate as the Audit Log: this is an audit view of assistant
 * usage). The totals card shows the FILTERED window's answer count, token
 * sums, and the estimated cost from the design §5 list prices — labelled
 * estimated because prompt-cache discounts aren't modelled.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import {
  v2,
  type AssistantActivityEntry,
  type AssistantActivityQuery,
  type AssistantActivityResponse,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { AssistantConfigNotice } from '@/components/assistant/AssistantConfigNotice';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { PermissionDenied } from '@/pages/PermissionDenied';
import { Spinner } from '@/components/dashboard/Spinner';
import { Pagination } from '@/components/dashboard/Pagination';

const PAGE_SIZE = 50;
const FREE_TEXT_DEBOUNCE_MS = 300;

const inputStyle: CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--text)',
};

// ui-closure — a compact inline label so dates/scope/message-type each have a
// visible (and accessible) name without changing the filter bar's flex layout.
const filterLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--text-muted)',
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 12,
  color: 'var(--text-muted)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
};

interface Filters {
  q: string;
  userEmail: string;
  scope: string;
  role: string;
  fromDate: string;
  toDate: string;
}

const EMPTY_FILTERS: Filters = { q: '', userEmail: '', scope: '', role: '', fromDate: '', toDate: '' };

function excerpt(content: string, max = 160): string {
  return content.length > max ? `${content.slice(0, max)}…` : content;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AssistantActivityPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('AuditLog', 'read');

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedQ, setAppliedQ] = useState('');
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState<AssistantActivityEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<{
    answers: number;
    tokensIn: number;
    tokensOut: number;
    estimatedCostUsd: number;
    unpricedAnswers?: number;
    pricingSource?: string | null;
    bySource?: AssistantActivityResponse['totals']['bySource'];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // phase-fix-assistant-activity-500 — bumping this refires the fetch (Retry).
  const [retryNonce, setRetryNonce] = useState(0);
  const debounceTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  // Debounce free-text search into the applied query.
  useEffect(() => {
    if (debounceTimer.current) globalThis.clearTimeout(debounceTimer.current);
    debounceTimer.current = globalThis.setTimeout(() => {
      setAppliedQ(filters.q.trim());
      setPage(1);
    }, FREE_TEXT_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) globalThis.clearTimeout(debounceTimer.current);
    };
  }, [filters.q]);

  const query = useMemo<AssistantActivityQuery>(() => {
    const next: AssistantActivityQuery = { page, pageSize: PAGE_SIZE, sort: 'CreatedAt:desc' };
    if (appliedQ) next.q = appliedQ;
    if (filters.userEmail.trim()) next.userEmail = filters.userEmail.trim().toLowerCase();
    if (filters.scope) next.scope = filters.scope;
    if (filters.role) next.role = filters.role;
    if (filters.fromDate) next.fromDate = new Date(filters.fromDate).toISOString();
    if (filters.toDate) {
      // End-of-day inclusive.
      const d = new Date(filters.toDate);
      d.setHours(23, 59, 59, 999);
      next.toDate = d.toISOString();
    }
    return next;
  }, [appliedQ, filters.userEmail, filters.scope, filters.role, filters.fromDate, filters.toDate, page]);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    setEntries(null);
    setError(null);
    v2.assistant
      .adminActivity(query)
      .then((res) => {
        if (cancelled) return;
        setEntries(res.entries);
        setTotal(res.total);
        setTotals(res.totals);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // phase-fix-assistant-activity-500 — the page is in exactly ONE
        // state (loading | loaded | error). A failed load renders the
        // error card only: no totals, no table, and NEVER the raw
        // response body (formatApiError discards SQL/driver internals).
        setError(formatApiError(err, 'Assistant activity could not be loaded.'));
        setEntries(null);
        setTotals(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canRead, query, retryNonce]);

  if (perms.loading) return <Spinner />;
  if (!canRead) return <PermissionDenied />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Assistant Activity</h1>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
        Every question and answer, with the model, routing, and token usage behind it.
        Tenant-scoped; retention follows Settings → Assistant.
      </p>

      {/* Test #9 — when the assistant is not usable, say so here too: an
          empty activity list must never pass for "nothing happened". */}
      <AssistantConfigNotice testId="assistant-activity-config-notice" />

      {totals && !error && (
        <div
          data-testid="assistant-activity-totals"
          style={{
            display: 'flex',
            gap: 24,
            padding: '12px 16px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Answers (filtered)</div>
            <strong data-testid="assistant-activity-total-answers">{totals.answers}</strong>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Tokens in</div>
            <strong data-testid="assistant-activity-total-tokens-in">
              {totals.tokensIn.toLocaleString()}
            </strong>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Tokens out</div>
            <strong data-testid="assistant-activity-total-tokens-out">
              {totals.tokensOut.toLocaleString()}
            </strong>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Estimated cost (all model use)
            </div>
            <strong data-testid="assistant-activity-total-cost">
              ${totals.estimatedCostUsd.toFixed(4)}
            </strong>
            {/* phase-classification-model-selection — classification spend is
                real spend. The entries table below is assistant Q&A by design
                (a classification has no conversation turn), so the cost figure
                is the COMBINED total and this line attributes it by source
                rather than leaving classifying invisible. */}
            {totals.bySource && (
              <div
                data-testid="assistant-activity-cost-by-source"
                style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}
              >
                Assistant ${totals.bySource.assistant.estimatedCostUsd.toFixed(4)} ·{' '}
                <span data-testid="assistant-activity-cost-classification">
                  Classification ${totals.bySource.classification.estimatedCostUsd.toFixed(4)}
                </span>{' '}
                ({totals.bySource.classification.calls} call
                {totals.bySource.classification.calls === 1 ? '' : 's'})
              </div>
            )}
            {totals.unpricedAnswers != null && totals.unpricedAnswers > 0 && (
              <div
                data-testid="assistant-activity-unpriced"
                style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}
              >
                {totals.unpricedAnswers} answer{totals.unpricedAnswers === 1 ? '' : 's'} on a
                retired model — not priced
              </div>
            )}
            {totals.pricingSource && (
              <div
                data-testid="assistant-activity-pricing-source"
                title={totals.pricingSource}
                style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}
              >
                Pricing: catalog
              </div>
            )}
          </div>
        </div>
      )}

      <div
        data-testid="assistant-activity-filters"
        style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
      >
        <input
          data-testid="assistant-activity-search"
          aria-label="Search content"
          placeholder="Search content…"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          style={{ ...inputStyle, minWidth: 220 }}
        />
        <input
          data-testid="assistant-activity-user"
          aria-label="User email"
          placeholder="User email"
          value={filters.userEmail}
          onChange={(e) => {
            setFilters((f) => ({ ...f, userEmail: e.target.value }));
            setPage(1);
          }}
          style={inputStyle}
        />
        <label style={filterLabelStyle}>
          Scope
          <select
            data-testid="assistant-activity-scope"
            value={filters.scope}
            onChange={(e) => {
              setFilters((f) => ({ ...f, scope: e.target.value }));
              setPage(1);
            }}
            style={inputStyle}
          >
            <option value="">All scopes</option>
            <option value="submission">Submission</option>
            <option value="global">All submissions</option>
            <option value="explanatory">General</option>
          </select>
        </label>
        <label style={filterLabelStyle}>
          Type
          <select
            data-testid="assistant-activity-role"
            value={filters.role}
            onChange={(e) => {
              setFilters((f) => ({ ...f, role: e.target.value }));
              setPage(1);
            }}
            style={inputStyle}
          >
            <option value="">Questions + answers</option>
            <option value="user">Questions only</option>
            <option value="assistant">Answers only</option>
          </select>
        </label>
        <label style={filterLabelStyle}>
          From
          <input
            data-testid="assistant-activity-from"
            type="date"
            value={filters.fromDate}
            onChange={(e) => {
              setFilters((f) => ({ ...f, fromDate: e.target.value }));
              setPage(1);
            }}
            style={inputStyle}
          />
        </label>
        <label style={filterLabelStyle}>
          To
          <input
            data-testid="assistant-activity-to"
            type="date"
            value={filters.toDate}
            onChange={(e) => {
              setFilters((f) => ({ ...f, toDate: e.target.value }));
              setPage(1);
            }}
            style={inputStyle}
          />
        </label>
      </div>

      {error && (
        <div
          data-testid="assistant-activity-error"
          role="alert"
          style={{
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#991b1b',
            borderRadius: 8,
            padding: '14px 16px',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            justifyContent: 'space-between',
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            data-testid="assistant-activity-retry"
            onClick={() => setRetryNonce((n) => n + 1)}
            style={{
              fontSize: 13,
              padding: '6px 14px',
              borderRadius: 6,
              border: '1px solid #991b1b',
              background: 'transparent',
              color: '#991b1b',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Retry
          </button>
        </div>
      )}
      {entries === null && !error && <Spinner />}

      {entries !== null && !error && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <ResponsiveTable data-testid="assistant-activity-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Scope</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Content</th>
                  <th style={thStyle}>Model</th>
                  <th style={thStyle}>Class</th>
                  <th style={thStyle}>Tokens</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr data-testid="assistant-activity-empty-row">
                    <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                      No assistant activity matches the filters.
                    </td>
                  </tr>
                )}
                {entries.map((e) => (
                  <tr key={e.Id} data-testid={`assistant-activity-row-${e.Id}`}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtTime(e.CreatedAt)}</td>
                    <td style={tdStyle}>{e.UserEmail}</td>
                    <td style={tdStyle}>{e.Scope}</td>
                    <td style={tdStyle}>{e.Role === 'assistant' ? 'Answer' : 'Question'}</td>
                    <td style={{ ...tdStyle, maxWidth: 420 }}><details><summary>{excerpt(e.Content)}</summary><p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.Content}</p></details></td>
                    <td style={tdStyle}>{e.Model ?? '—'}</td>
                    <td style={tdStyle}>
                      {e.RoutedClass ?? '—'}
                      {e.Overridden ? ' (override)' : ''}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {e.TokensIn != null || e.TokensOut != null
                        ? `${e.TokensIn ?? 0} / ${e.TokensOut ?? 0}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
          <div style={{ marginTop: 12 }}>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </>
      )}
    </div>
  );
}
